import type {
  CompletedRunResponse,
  NoNewDataRunResponse,
  RecentlyRanRunResponse,
  RunDiagnosticsResponse,
} from '@prerender/diagnostics-api';

import { formatDateWithAge } from './formatDateWithAge';
import { formatDetectedAt } from './formatDetectedAt';
import { incidentTypeLabel } from './incidentTypeLabels';

export type RunNotice = {
  variant: 'success' | 'info' | 'warning';
  line: string;
};

const counted = (count: number, singular: string): string => `${count} ${singular}${count === 1 ? '' : 's'}`;

const NOTHING_CHANGED = 'nothing changed';

const changesOf = (run: CompletedRunResponse): string => {
  const parts = [
    run.created > 0 ? counted(run.created, 'new incident') : null,
    run.updated > 0 ? `${run.updated} updated` : null,
    run.skippedChecks > 0 ? `${counted(run.skippedChecks, 'incident')} skipped` : null,
  ].filter((part): part is string => part !== null);

  return parts.length === 0 ? NOTHING_CHANGED : parts.join(', ');
};

const NO_DATA_YET = 'No new data — detection has not seen any data yet';

const nothingNewSince = (run: NoNewDataRunResponse, now: Date): string => {
  if (run.watermark === null) {
    return NO_DATA_YET;
  }

  return `No new data since ${formatDateWithAge(new Date(run.watermark), now)}`;
};

const alreadyRanOn = (run: RecentlyRanRunResponse, now: Date): string =>
  `Detection already ran on data up to ${formatDateWithAge(new Date(run.lastWatermark), now)}. ` +
  `It can run again after ${formatDetectedAt(new Date(run.nextRunAfter))}.`;

const failureSentence = (failedDetectors: readonly string[]): string => {
  const names = failedDetectors.map(incidentTypeLabel);
  const allButLast = names.slice(0, -1);
  const last = names[names.length - 1];

  return `${allButLast.length === 0 ? last : `${allButLast.join(', ')} and ${last}`} failed.`;
};

const RAN = 'Detection ran';
const PARTLY_RAN = 'Detection partly ran';

const summaryOf = (run: RunDiagnosticsResponse, now: Date, someDetectorFailed: boolean): string => {
  switch (run.outcome) {
    case 'no-new-data':
      return nothingNewSince(run, now);
    case 'recently-ran':
      return alreadyRanOn(run, now);
    case 'completed':
      return `${someDetectorFailed ? PARTLY_RAN : RAN}: ${changesOf(run)}`;
    default: {
      const exhaustive: never = run;
      throw new Error(`Unhandled run outcome ${JSON.stringify(exhaustive)}`);
    }
  }
};

const toneOf = (run: RunDiagnosticsResponse, someDetectorFailed: boolean): RunNotice['variant'] => {
  if (someDetectorFailed) {
    return 'warning';
  }

  return run.outcome === 'completed' ? 'success' : 'info';
};

const closedNotPayingSentence = (run: RunDiagnosticsResponse): string | null =>
  run.closedNotPaying > 0 ? `${counted(run.closedNotPaying, 'incident')} closed — account no longer paying` : null;

export function runOutcomeNotice(run: RunDiagnosticsResponse, now: Date): RunNotice {
  const someDetectorFailed = run.failedDetectors.length > 0;
  const summary = summaryOf(run, now, someDetectorFailed);
  const withFailures = someDetectorFailed ? `${summary}. ${failureSentence(run.failedDetectors)}` : summary;
  const closed = closedNotPayingSentence(run);

  return {
    variant: toneOf(run, someDetectorFailed),
    line: closed === null ? withFailures : `${withFailures}. ${closed}`,
  };
}
